import {
  getApiAdminCompanyByCompanyTreeSettingsIdQuery,
  invalidateAdminCompanyTreeQueryData,
  updateAdminCompanyTreeQueryData,
  useGetApiAdminJobStatus,
} from '@sit/client-shared';
import { skipToken, useQueryClient } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import Card, {
  CardColumn,
  CardFooter,
  CardRow,
} from 'carbon-react/lib/components/card';
import Dialog from 'carbon-react/lib/components/dialog';
import Divider from 'carbon-react/lib/components/divider';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Message from 'carbon-react/lib/components/message';
import Typography from 'carbon-react/lib/components/typography';
import { format, isValid, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { send } from '../../../helpers/requests';
import toastr from '../../../helpers/toastr';

export interface CompanyActivationCardProps {
  adminEmail: string | null;
  adminUsername: string | null;
  archivedOn: string | null;
  companyTreeSettingsId: number;
  intacctCompanyId: string;
  reactivatedOn: string | null;
}

const formatTimestamp = (value: string) => {
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'PPp') : null;
};

export function CompanyActivationCard({
  adminEmail,
  adminUsername,
  archivedOn,
  companyTreeSettingsId,
  intacctCompanyId,
  reactivatedOn,
}: CompanyActivationCardProps) {
  const queryClient = useQueryClient();
  const companyQuery = getApiAdminCompanyByCompanyTreeSettingsIdQuery({
    pathParams: {
      id: `${companyTreeSettingsId}`,
    },
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [archiveJobId, setArchiveJobId] = useState<string>();

  const isArchived = archivedOn != null;
  const canChangeStatus = !!adminEmail && !!adminUsername;
  const isArchivePending = archiveJobId != null;

  const actionCopy = useMemo(() => {
    if (isArchived) {
      return {
        actionLabel: 'Reactivate company',
        confirmLabel: 'Reactivate',
        dialogTitle: 'Reactivate company',
        requestUrl: '/admin/unarchive-company',
        successMessage: `Reactivated ${intacctCompanyId}.`,
        summary:
          'This company is currently deactivated and cannot be used until it is reactivated.',
      };
    }

    return {
      actionLabel: 'Deactivate company',
      confirmLabel: 'Deactivate',
      dialogTitle: 'Deactivate company',
      requestUrl: '/admin/archive-company',
      successMessage: `Deactivated ${intacctCompanyId}.`,
      summary:
        'This company is currently active and available to its Sage Intelligent Time users.',
    };
  }, [intacctCompanyId, isArchived]);

  const lastStatusChange = isArchived ? archivedOn : reactivatedOn;
  const formattedLastStatusChange = lastStatusChange
    ? formatTimestamp(lastStatusChange)
    : null;

  const statusLabel = isArchived ? 'Deactivated' : 'Active';
  const statusTone = isArchived ? 'warning' : 'info';

  const refreshCompanyData = async () => {
    await Promise.all([
      invalidateAdminCompanyTreeQueryData(queryClient),
      queryClient.invalidateQueries({
        queryKey: companyQuery.queryKey,
      }),
    ]);

    await Promise.all([
      queryClient.refetchQueries({
        queryKey: ['admin', 'companies'],
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: companyQuery.queryKey,
        exact: true,
      }),
    ]);
  };

  useGetApiAdminJobStatus(
    archiveJobId
      ? {
          queryParams: {
            jobId: archiveJobId,
            queueName: 'archive-company-tree',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 2_000,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          void refreshCompanyData();
          toastr.success(`Deactivated ${intacctCompanyId}.`);
          setArchiveJobId(undefined);
          setIsPending(false);
          return false;
        }
        if (!(error instanceof Error)) {
          setArchiveJobId(undefined);
          setIsPending(false);
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        toastr.error(error.message);
        setArchiveJobId(undefined);
        setIsPending(false);
        return false;
      },
    },
  );

  const submitStatusChange = async () => {
    if (!adminEmail || !adminUsername || isPending) return;

    setIsPending(true);
    try {
      const requestData = {
        intacctCompanyId,
        username: adminUsername,
        email: adminEmail,
      };

      if (isArchived) {
        await send({
          method: 'POST',
          url: actionCopy.requestUrl,
          data: requestData,
          credentials: 'include',
        });
        updateAdminCompanyTreeQueryData(
          queryClient,
          companyTreeSettingsId,
          (cts) => ({
            ...cts,
            archivedOn: null,
            reactivatedOn: new Date().toISOString(),
          }),
        );
        await refreshCompanyData();
        toastr.success(actionCopy.successMessage);
        setIsConfirmOpen(false);
        setIsPending(false);
        return;
      }

      const response: unknown = await send({
        method: 'POST',
        url: actionCopy.requestUrl,
        data: requestData,
        credentials: 'include',
      });

      if (
        !response ||
        typeof response !== 'object' ||
        !('jobId' in response) ||
        typeof response.jobId !== 'string'
      ) {
        throw new Error('Archive request did not return a job ID.');
      }

      setArchiveJobId(response.jobId);
      setIsConfirmOpen(false);
      toastr.info(`Deactivation requested for ${intacctCompanyId}.`);
    } catch (error) {
      toastr.error(
        error instanceof Error
          ? error.message
          : `Failed to ${isArchived ? 'reactivate' : 'deactivate'} company.`,
      );
      setIsPending(false);
    }
  };

  return (
    <>
      <Card
        footer={
          <CardFooter px={1} py={1}>
            <Box width="100%" display="flex" justifyContent="flex-end">
              <Button
                disabled={!canChangeStatus || isPending || isArchivePending}
                onClick={() => setIsConfirmOpen(true)}
                variant={isArchived ? undefined : 'destructive'}
                variantType={isArchived ? undefined : 'secondary'}
              >
                {isPending ? (
                  <Loader
                    loaderType="ring"
                    variant="inline"
                    size="small"
                    loaderLabel={
                      isArchived ? 'Reactivating...' : 'Deactivating...'
                    }
                  />
                ) : (
                  actionCopy.actionLabel
                )}
              </Button>
            </Box>
          </CardFooter>
        }
      >
        <CardRow pb={0}>
          <CardColumn align="left">
            <Typography variant="h1" mt={2}>
              Company access
            </Typography>
          </CardColumn>
        </CardRow>
        <Divider type="horizontal" mt={2} mb={2} />
        <CardRow>
          <CardColumn align="left">
            <Box display="flex" flexDirection="column" gap={2}>
              {isArchivePending && (
                <Message variant="info">
                  Deactivation is in progress. This card will refresh when the
                  background job completes.
                </Message>
              )}
              <Message variant={statusTone}>{statusLabel}</Message>
              <Typography>{actionCopy.summary}</Typography>
              {formattedLastStatusChange && (
                <Typography color="secondary">
                  {isArchived ? 'Deactivated on' : 'Last reactivated on'}{' '}
                  {formattedLastStatusChange}
                </Typography>
              )}
              {!canChangeStatus && (
                <Message variant="warning">
                  This action needs both the admin email and admin username to
                  be present on the company.
                </Message>
              )}
            </Box>
          </CardColumn>
        </CardRow>
      </Card>
      <Dialog
        open={isConfirmOpen}
        onCancel={() => {
          if (!isPending) {
            setIsConfirmOpen(false);
          }
        }}
        title={actionCopy.dialogTitle}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography>
            {isArchived
              ? `Reactivate ${intacctCompanyId} so users can access Sage Intelligent Time again?`
              : `Deactivate ${intacctCompanyId}? This will remove access to Sage Intelligent Time until the company is reactivated.`}
          </Typography>
          <Typography color="secondary">
            Admin notification will be sent using{' '}
            {adminEmail ?? 'the saved admin email once available'}.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variantType="secondary"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void submitStatusChange();
              }}
              variant={isArchived ? undefined : 'destructive'}
              variantType={isArchived ? undefined : 'secondary'}
              disabled={isPending}
            >
              {isPending ? (
                <Loader
                  loaderType="ring"
                  variant="inline"
                  size="small"
                  loaderLabel={
                    isArchived ? 'Reactivating...' : 'Deactivating...'
                  }
                />
              ) : (
                actionCopy.confirmLabel
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
